/* JV purchase-first layout. No stock message, cart interception or price rewriting. */
(() => {
  if (typeof window === 'undefined' || customElements.get('jv-purchase-layout')) return;
  class JVPurchaseLayout extends HTMLElement {
    connectedCallback() { this.schedule(); }
    disconnectedCallback() { cancelAnimationFrame(this.frame); this.unmount(); }
    schedule() { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.mount()); }
    message(text) {
      const note = document.querySelector(`[data-jvp-editor="${CSS.escape(this.dataset.sectionId)}"]`);
      if (note) { note.textContent = 'JV Purchase Layout: ' + text; note.hidden = !text; }
    }
    mount() {
      if (!this.isConnected || this.hero?.isConnected) return;
      this.unmount();
      let config;
      try { config = JSON.parse(this.querySelector('[data-jvp-config]').textContent); }
      catch { this.message('Invalid configuration; existing hero unchanged.'); return; }
      const wrapper = this.closest('.shopify-section');
      let previous = wrapper?.previousElementSibling;
      const additions = previous?.querySelector('jv-product-additions');
      if (additions) {
        if (!customElements.get('jv-product-additions')) {
          customElements.whenDefined('jv-product-additions').then(() => { if (this.isConnected) this.schedule(); });
          this.message('Waiting for Product Additions to load.'); return;
        }
        additions.mount?.();
        previous = previous.previousElementSibling;
      }
      const hero = previous?.querySelector('jv-product-hero-v2');
      const candidates = [...document.querySelectorAll('jv-product-hero-v2')].filter(h => h.dataset.productId === this.dataset.productId);
      const info = hero?.querySelector('.jv-fp__info');
      const price = info?.querySelector(':scope > .jv-fp__price-line');
      const form = info?.querySelector('form.jv-fp__form');
      const buy = form?.querySelector('[data-jv-fp-atc]');
      if (!hero || candidates.length !== 1 || candidates[0] !== hero || hero.hasAttribute('data-jvp-layout') || !info?.querySelector(':scope > .jv-fp__title') || !price || !form?.querySelector('[name="id"]') || !buy || (additions && additions.hero !== hero)) {
        this.message('Place immediately after the matching V2 hero / its Product Additions. Unsupported or duplicate heroes are left unchanged.'); return;
      }
      this.hero = hero; this.info = info; this.form = form; this.additions = additions; this.config = config; this.markers = []; this.generated = [];
      this.controller = new AbortController();
      this.on = (el, type, fn, options = {}) => el?.addEventListener(type, fn, {...options, signal:this.controller.signal});
      this.slots = Object.fromEntries([...this.querySelectorAll('[data-jvp-slot]')].map(n => [n.dataset.jvpSlot,n]));
      hero.setAttribute('data-jvp-layout', this.id);
      if (this.slots.rating) this.move(this.slots.rating, price);
      // Preserve the original native links, More dialog and all attached listeners.
      // Product Additions already owns this relocation when it is present.
      if (!additions) {
        let anchor = price;
        for (const node of [...info.children].filter(n => n.matches('.jv-fp__variation-label,.jv-fp__var-row'))) {
          this.move(node, info); anchor.after(node); anchor = node;
        }
      }
      let afterBuy = form;
      // Keep native shipping/subscription notes in place; payment icons follow them.
      while (afterBuy.nextElementSibling?.matches('.jv-fp__note')) afterBuy = afterBuy.nextElementSibling;
      if (config.showPayments) {
        const payments = additions?.below?.querySelector('.jva-payments') || this.slots.payments;
        if (payments) {
          const paymentRow = document.createElement('div'); paymentRow.className = 'jvp-payment-row';
          afterBuy.after(paymentRow); this.generated.push(paymentRow);
          this.move(payments,paymentRow); afterBuy = paymentRow;
        }
      }
      // Move the original live panel, not a static copy. The updated additions
      // controller retains its reference for country, variant and date updates.
      const delivery = additions?.delivery;
      if (delivery && additions.below?.contains(delivery) && (config.showPayments || !additions.below.querySelector('.jva-payments'))) {
        const deliveryRow = document.createElement('div'); deliveryRow.className = 'jva jvp-delivery-row';
        afterBuy.after(deliveryRow); this.generated.push(deliveryRow);
        this.move(delivery, deliveryRow); afterBuy = deliveryRow;
      }
      for (const key of ['reviews','about']) { this.move(this.slots[key], info); afterBuy.after(this.slots[key]); afterBuy = this.slots[key]; }
      this.intro = info.querySelector(':scope > .jv-fp__intro');
      if (this.intro) {
        this.introWasHidden = this.intro.hidden;
        this.move(this.intro, this.slots.about.querySelector('[data-jvp-original-intro]'));
        if (config.customAbout) this.intro.hidden = true;
      }
      // Existing additions keep ownership of their below-additions spec row.
      if (!additions) [...info.children].filter(n => n.matches('.jv-fp__specs,[data-jv-spec-link]')).forEach(n => this.move(n,this.slots.about));
      this.cards = [...this.slots.reviews.querySelectorAll('[data-jvp-review]')];
      this.reviewIndex = 0; this.showReview(0);
      this.slots.reviews.querySelector('[data-jvp-controls]').hidden = this.cards.length < 2;
      this.slots.reviews.querySelectorAll('[data-jvp-review-step]').forEach(b => this.on(b,'click',() => this.showReview(this.reviewIndex + Number(b.dataset.jvpReviewStep))));
      this.on(document,'shopify:block:select',event => {
        const i = this.cards.findIndex(c => c.dataset.blockId === event.detail?.blockId);
        if (i >= 0 && event.detail?.sectionId === this.dataset.sectionId) { this.showReview(i); this.cards[i].scrollIntoView({block:'nearest',behavior:'instant'}); }
      });
      this.on(document,'shopify:section:select',event => { if (event.detail?.sectionId === this.dataset.sectionId) this.info.scrollIntoView({block:'start',behavior:'instant'}); });
      this.message(additions?.below?.querySelector('.jva-delivery') && !additions.delivery ? 'Update assets/jv-product-additions.js as well to move the live delivery panel below payments.' : '');
    }
    move(node, destination) {
      if (!node || !destination) return;
      const marker = document.createComment('JV purchase layout restore'); node.before(marker);
      this.markers.push({node,marker}); destination.append(node);
    }
    showReview(index) {
      if (!this.cards.length) return;
      this.reviewIndex = (index + this.cards.length) % this.cards.length;
      this.cards.forEach((card,i) => card.hidden = i !== this.reviewIndex);
      this.slots.reviews.querySelector('[data-jvp-review-position]').textContent = `${this.reviewIndex + 1} / ${this.cards.length}`;
    }
    unmount() {
      this.controller?.abort();
      if (this.intro) this.intro.hidden = this.introWasHidden;
      this.cards?.forEach(c => c.hidden = false);
      for (const {node,marker} of [...this.markers || []].reverse()) { if (marker.parentNode) marker.replaceWith(node); }
      this.generated?.forEach(n => n.remove());
      this.hero?.removeAttribute('data-jvp-layout');
      this.hero = null; this.info = null; this.form = null; this.markers = []; this.generated = []; this.intro = null; this.cards = null;
    }
  }
  customElements.define('jv-purchase-layout',JVPurchaseLayout);
  document.addEventListener('shopify:section:unload',event => {
    document.querySelectorAll('jv-purchase-layout').forEach(layout => {
      if (event.target.contains(layout) || event.target.contains(layout.hero) || event.target.contains(layout.additions)) { layout.unmount(); layout.message('Waiting for the matching hero / additions.'); }
    });
  });
  document.addEventListener('shopify:section:load',() => document.querySelectorAll('jv-purchase-layout').forEach(layout => { layout.unmount(); layout.schedule(); }));
  document.addEventListener('shopify:section:reorder',() => document.querySelectorAll('jv-purchase-layout').forEach(layout => { layout.unmount(); layout.schedule(); }));
})();